import React from 'react';
import PropTypes from 'prop-types';
import { accessibility } from 'accessible-react-table';
import ReactTable from 'react-table';
import { CSVLink } from 'react-csv';
import ReactDOMServer from 'react-dom/server';

import { fileSlug } from '../../utils/index';
import { getColumns } from '../../utils/table';

import './index.scss';

class SimpleDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: this.expandAll()
    };
    this.domNode = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { expandLevel } = this.props;
    if (prevProps.expandLevel !== expandLevel) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ expanded: this.expandAll() });
    }
  }

  onExpandedChange = (expanded) => {
    this.setState({ expanded });
  };

  /**
   * The following function sets up the variable needed for the data download.
   * It creates a filename, header object, and data object and returns
   * a csv download link of the data in the table.
  * */
  getDataDownload = (download, data, headers, total) => {
    const fileName = download ? fileSlug(download) : fileSlug('data-download');
    const { title } = this.props;
    const dldData = [];
    // If a cell is a react component, extract just the rendered text from the cell for the data download.
    const dldDataMax = data.length;
    let i = 0;
    for (; i < dldDataMax; i += 1) {
      const row = data[i];
      dldData[i] = { ...data[i] };
      const rowKeys = Object.keys(row);
      const rowKeysMax = rowKeys.length;
      let j = 0;
      for (; j < rowKeysMax; j += 1) {
        const key = rowKeys[j];
        if (row[key]) {
          if ((row[key]).$$typeof) {
            const renderedHtml = ReactDOMServer.renderToString(data[i][key]);
            const justText = renderedHtml.replace(/<[^>]+>/g, '').replace(/&#x27;/g, '\'');
            dldData[i][key] = justText;
          } else {
            dldData[i][key] = data[i][key];
          }
        }
      }
    }
    const dldHeaders = headers.map((header) => ({ label: header.name, key: header.value }));
    // If the table has total rendered in the footer, append this as the last row of data.
    if (total) {
      // If total object is wrapped in an array, e.g. line items
      if (Array.isArray(total)) {
        dldData.push(total[0]);
      } else {
        dldData.push(total);
      }
    }
    return(
      <div className="ma__data-download">
        <CSVLink data={dldData} headers={dldHeaders} filename={fileName}>
          Download the
          <span className="ma-visually-hidden">
            {title}
          </span>
          {' '}
          data
          {' '}
          <span aria-hidden>↓</span>
        </CSVLink>
      </div>
    );
  };

  expandIt = ({ data, remaining }) => {
    const { expandLevel } = this.props;
    const remain = remaining || expandLevel;
    const expandThese = {};
    if (remain > 0) {
      Object.keys(data)
        .filter((value) => !Number.isNaN(value))
        .forEach((rowIndex) => {
          if (remain > 0 && data[rowIndex].values) {
            expandThese[rowIndex] = this.expandIt({
              data: data[rowIndex].values,
              remaining: remain - 1
            });
          } else {
            expandThese[rowIndex] = {};
          }
        });
    }
    return expandThese;
  };

  handleSubComponent = (row, columns) => {
    const { getSubComponent, activeButton } = this.props;
    if (typeof getSubComponent === 'function') {
      return getSubComponent(row, columns, activeButton);
    }
    return null;
  }

  expandAll() {
    const { table: { data } } = this.props;
    return this.expandIt({ data });
  }

  render() {
    const { getSubComponent, renderSubComponent, table } = this.props;
    const { expanded } = this.state;
    const tableClasses = (!getSubComponent || !renderSubComponent) ? '-striped -highlight ma__simple-table' : '-striped -highlight ma__simple-table ma__simple-table--subcomponent';
    const {
      maxColWidth,
      setMaxColWidth = Object.prototype.hasOwnProperty.call(this.props, 'maxColWidth'),
      mantissa,
      columns = getColumns(table, {
        aggregateBy: '', setMaxColWidth, maxColWidth, mantissa
      }),
      className = tableClasses,
      pagination,
      pageSize,
      download,
      ...rest
    } = this.props;
    const {
      data, headers, total, filterable
    } = table;
    const tableProps = {
      columns,
      data,
      showPaginationBottom: pagination || false,
      className,
      minRows: pagination ? 5 : data.length,
      getTrProps: (state, rowInfo) => ({
        className: (rowInfo && rowInfo.row) ? rowInfo.row.rowType : ''
      }),
      defaultPageSize: pageSize || data.length,
      pageSizeOptions: [5, 10, 20, 25, 50, 100, data.length],
      onPageChange: () => {
        if (this.domNode && this.domNode.current) {
          // eslint-disable-next-line no-undef
          const topTbl = (this.domNode.current.getBoundingClientRect().top + window.pageYOffset) - 50;
          // eslint-disable-next-line no-undef
          window.scrollTo(0, topTbl);
        }
      },
      SubComponent: getSubComponent && renderSubComponent && ((row) => this.handleSubComponent(row, columns)),
      filterable,
      expanded,
      onExpandedChange: this.onExpandedChange,
      ...rest
    };
    const AccessibleReactTable = accessibility(ReactTable);
    return(
      <div ref={this.domNode}>
        <AccessibleReactTable {...tableProps} />
        {download && this.getDataDownload(download, data, headers, total)}
      </div>
    );
  }
}

SimpleDataTable.propTypes = {
  title: PropTypes.string,
  maxColWidth: PropTypes.number,
  setMaxColWidth: PropTypes.bool,
  mantissa: PropTypes.number,
  download: PropTypes.string,
  className: PropTypes.string,
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  table: PropTypes.object,
  columns: PropTypes.object,
  /** How many levels of the simpledata table you would like to be expanded, starting a level 0. */
  expandLevel: PropTypes.number,
  /** This function receive the row data for the subcomponent. Whatever it returns will be rendered
    * in a collapsable subrow of the table.
  * */
  getSubComponent: PropTypes.func,
  activeButton: PropTypes.string,
  /** Whether to call getSubComponent and render SubComponent. */
  renderSubComponent: PropTypes.bool
};

export default SimpleDataTable;
